// File: PropertyAnalyticsSection.js
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import InfoIcon from '@mui/icons-material/Info'
import LocalMallIcon from '@mui/icons-material/LocalMall'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ParkIcon from '@mui/icons-material/Park'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import SchoolIcon from '@mui/icons-material/School'
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer'

const CATEGORY_ICONS = {
  parks: <ParkIcon />,
  retail_dining: <RestaurantIcon />,
  schools: <SchoolIcon />,
  public_transport: <DirectionsBusIcon />,
  playgrounds: <SportsSoccerIcon />,
  healthcare: <LocalPharmacyIcon />,
}

const getCategoryIcon = (category) => CATEGORY_ICONS[category] || <InfoIcon color="disabled" />

// Use your existing backend URL logic or just import from env:
const backendURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'

export default function PropertyAnalyticsSection({ listing, userToken }) {
  // ----- STATES -----
  const [analyticsResult, setAnalyticsResult] = useState(null)
  const [featuresResult, setFeaturesResult] = useState(null)

  // We'll separate loading states to manage them independently
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [featuresLoading, setFeaturesLoading] = useState(false)

  const [error, setError] = useState('')
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false) // For the main accordion

  const radius = 500

  // ----- HANDLERS (FETCH) -----
  const handleAnalyticsFetch = async () => {
    setAnalyticsLoading(true)
    setError('')
    setAnalyticsResult(null)

    try {
      const url = new URL(`${backendURL}/analytics/analytics`)
      const latitude = listing.latitude
      const longitude = listing.longitude
      url.search = new URLSearchParams({
        latitude: latitude,
        longitude: longitude,
        radius,
      }).toString()

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${userToken}` },
      })
      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`)
      }
      const data = await response.json()
      setAnalyticsResult(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setAnalyticsLoading(false)
    }
  }

  // ----- EFFECTS -----
  useEffect(() => {
    if (listing) {
      // Fetch analytics data immediately
      handleAnalyticsFetch()
      // DO NOT FETCH features here: we now fetch on demand when user expands
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing])

  // ----- HELPERS -----
  const getFeatureCategories = (data) => {
    if (!data?.features) return []
    return Object.entries(data.features).map(([category, val]) => {
      const items = Array.isArray(val) ? val : []
      return { category, items }
    })
  }

  const renderCounts = (counts) => {
    if (!counts) return null

    const getIconForKey = (key) => {
      if (key === 'parks') return <ParkIcon fontSize="small" />
      if (key === 'retail_dining') return <RestaurantIcon fontSize="small" />
      if (key === 'schools') return <SchoolIcon fontSize="small" />
      if (key === 'public_transport') return <DirectionsBusIcon fontSize="small" />
      if (key === 'playgrounds') return <SportsSoccerIcon fontSize="small" />
      if (key === 'healthcare') return <LocalPharmacyIcon fontSize="small" />
      return <LocalMallIcon fontSize="small" />
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {Object.entries(counts).map(([key, value]) => (
          <Tooltip key={key} title={`Number of ${key}`} arrow>
            <Chip
              icon={getIconForKey(key)}
              label={`${key}: ${value}`}
              variant="outlined"
              color="primary"
              sx={{ textTransform: 'capitalize' }}
            />
          </Tooltip>
        ))}
      </Box>
    )
  }

  // ----- RENDER -----
  return (
    <Box sx={{ mt: 4 }}>
      {/* Title */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Property Analytics
      </Typography>

      {/* Error Notification */}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* ----- Analytics Overview (Always Visible) ----- */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Analytics Overview
        </Typography>

        {/* Loading Spinner for analytics */}
        {analyticsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Show analytics if loaded */}
        {analyticsResult && !analyticsLoading && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ backgroundColor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Overall Property Score
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 2,
                    }}
                  >
                    <Typography variant="h2" color="primary" sx={{ fontWeight: 'bold' }}>
                      {analyticsResult.score}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    A custom metric reflecting location attractiveness.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ backgroundColor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Closest School
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <SchoolIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {analyticsResult.distance_to_closest_school
                        ? `${analyticsResult.distance_to_closest_school.toFixed(2)}m`
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Distance to the nearest school
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Nearby Amenity Counts */}
            <Grid item xs={12}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Nearby Amenity Counts (within {radius}m)
                </Typography>
                {renderCounts(analyticsResult.counts)}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  )
}
